import { Fragment, memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { casePropTypes } from 'common/propTypes';
import { signalTypes } from 'v2/modules/withRunForm/configs/index';
import { useCase } from 'v2/modules/withRunForm/hooks/useCase/index';
import ErrorMesssage from 'v2/common/components/ErrorMessage/index';
import Signals from 'v2/modules/withRunForm/components/Signals/index';
import Intents from 'v2/modules/withRunForm/components/Intents/index';
import HoverMessage from 'common/components/HoverMessage';
import { REFERENCE_LIST } from 'v2/common/constants';
import CaseBasedFeatureFlags from './CaseBasedBehaviourFlags/index';
import { referenceTracker } from 'v3/quantConfig/reference/referenceTracker';

const propTypes = {
  caseConfig: casePropTypes.isRequired,
  caseIndex: PropTypes.number.isRequired,
  errorMessage: PropTypes.string,
  errorConfigs: PropTypes.shape({}),
  isHiddenSignal: PropTypes.bool.isRequired,
  isSimpleMode: PropTypes.bool.isRequired,
};
const defaultProps = {
  errorMessage: '',
  errorConfigs: {},
};

const Case = ({ caseIndex, caseConfig, errorConfigs, isHiddenSignal }) => {
  const {
    entrySignals,
    exitSignals,
    intents,
    onUpdateEntrySignals,
    onUpdateExitSignals,
    onUpdateIntents,
    uiConfigs,
    onUpdateUiConfigs,
    isValidToEnableMoveToCost,
    adjustments,
    onAddAdjustment,
    onCloneAdjustment,
    onUpdateAdjustmentSignals,
    onUpdateAdjustmentIntents,
    onDeleteAdjustment,
    getModifyLegsTargetSchema,
    getModifyCasesTargetSchema,
  } = useCase(caseConfig, caseIndex);

  const adjustmentErrorMessage = errorConfigs[caseIndex]?.adjustments?.errorMessage || '';
  const isPremiumError = errorConfigs[caseIndex]?.adjustments?.isPremiumError || false;
  const modifyCasesTargetSchema = useMemo(() => {
    return getModifyCasesTargetSchema(caseIndex);
  }, [getModifyCasesTargetSchema, caseIndex]);
  const isPrivilegedUser = _.get(window, 'isPrivilegedUser', false);

  const renderSignals = useCallback(
    (signals, signalType, onUpdateSignals, extraArgs = {}) => {
      const signalProps = {
        signals,
        caseIndex,
        signalType,
        onUpdateSignals,
        isHiddenSignal,
        ...extraArgs,
      };

      return <Signals {...signalProps} />;
    },
    [caseIndex, isHiddenSignal]
  );

  const handleDeleteAdjustment = useCallback(
    (adjustmentIndex) => {
      const adjustmentEntity = {
        type: REFERENCE_LIST.adjustment,
        index: adjustmentIndex,
        details: adjustments[adjustmentIndex],
      };
      if (adjustments[adjustmentIndex].intents.length > 0) {
        referenceTracker.performDeleteOrShowWarning(adjustmentEntity, () =>
          onDeleteAdjustment(adjustmentIndex)
        );
      } else {
        onDeleteAdjustment(adjustmentIndex);
      }
    },
    [onDeleteAdjustment]
  );

  const renderMoveToCostCheckbox = useCallback(() => {
    return (
      <CaseBasedFeatureFlags
        uiConfigs={uiConfigs}
        onUpdateUiConfigs={onUpdateUiConfigs}
        isValidToEnableMoveToCost={isValidToEnableMoveToCost}
      />
    );
  }, [uiConfigs, onUpdateUiConfigs, isValidToEnableMoveToCost]);

  const renderAdjustment = useCallback(
    (adjustment, adjustmentIndex) => {
      const { signal, intents: adjustIntents } = adjustment;
      const modifyLegsTargetSchema = getModifyLegsTargetSchema(adjustmentIndex);
      // Note: Not a callback, new function everytime.
      const onUpdateIntentsForAdjustment = (newIntents, removedOpenIntentIds = []) => {
        return onUpdateAdjustmentIntents(newIntents, removedOpenIntentIds, adjustmentIndex);
      };

      return (
        <Fragment key={adjustmentIndex}>
          <div className="adjust-signal-container">
            {isPremiumError && <img src="/v3Crown.png" className="ajustment-crown v2-crown-image" alt="" />}
            <span className="adjustment-number-badge badge-secondary">
              Adjustment&nbsp;
              {adjustmentIndex + 1}
            </span>
            {renderSignals(
              signal,
              signalTypes.adjustSignal,
              (newSignals) => onUpdateAdjustmentSignals(newSignals, adjustmentIndex),
              { adjustmentIndex }
            )}
            <div className='adustment-actions'>
              <button className="delete-button btn btn-outline-secondary "
                type="button" onClick={() => onCloneAdjustment(adjustment)}>
                <i className="material-icons-outlined tx-16" >file_copy</i>
              </button>
            <button
              className="delete-button btn btn-outline-secondary "
              type="button"
              onClick={() => handleDeleteAdjustment(adjustmentIndex)}
            >
              {/* <i aria-disabled="false" /> */}
              <i className="fa-regular fa-trash-can tx-13" aria-disabled="false"></i>
            </button>
           </div>
          </div>
          <Intents
            intents={adjustIntents}
            caseIndex={caseIndex}
            onUpdateIntents={onUpdateIntentsForAdjustment}
            isAdjustmentIntent
            adjustmentIndex={adjustmentIndex}
            modifyLegsTargetSchema={modifyLegsTargetSchema}
            modifyCasesTargetSchema={modifyCasesTargetSchema}
          />
        </Fragment>
      );
    },
    [
      caseIndex,
      getModifyLegsTargetSchema,
      isPremiumError,
      onDeleteAdjustment,
      onUpdateAdjustmentIntents,
      onUpdateAdjustmentSignals,
      renderSignals,
      getModifyCasesTargetSchema,
    ]
  );

  const renderAddAdjustmentButton = useMemo(() => {
    return (
      <button
        type="button"
        disabled={isHiddenSignal}
        className="btn btn-primary d-flex
                align-items-center"
        onClick={() => {
          if (!isHiddenSignal) onAddAdjustment();
        }}
      >
        <i className="fa-regular fa-square-plus me-1" aria-hidden="true"></i>
        Add Adjustment
      </button>
    );
  }, [isHiddenSignal, onAddAdjustment]);

  return (
    <>
      <div className="case-container p-1 p-sm-2 tabs-info">
        {renderSignals(entrySignals, signalTypes.entrySignal, onUpdateEntrySignals)}
        {renderSignals(exitSignals, signalTypes.exitSignal, onUpdateExitSignals)}
        <Intents
          caseIndex={caseIndex}
          intents={intents}
          onUpdateIntents={onUpdateIntents}
          renderMoveToCostCheckbox={renderMoveToCostCheckbox}
          modifyCasesTargetSchema={modifyCasesTargetSchema}
        />
        <ErrorMesssage message={adjustmentErrorMessage} isPremiumError={isPremiumError} />
        {adjustments.map(renderAdjustment)}
        <div className="add-adjustment-container d-flex justify-content-center py-2">
          {isHiddenSignal ? (
            <HoverMessage message="This action has been disabled by the Author">
              {renderAddAdjustmentButton}
            </HoverMessage>
          ) : (
            renderAddAdjustmentButton
          )}
        </div>
      </div>
    </>
  );
};

Case.propTypes = propTypes;
Case.defaultProps = defaultProps;

export default memo(Case);
